import React from 'react';
import {
  Button,
  Box,
  Grid,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  type Organization,
  type Department,
} from './graphql/useGetAllOrganizations';
import { getStatus } from './utils/status';

type DepartmentViewProps = {
  data: Organization[];
  onAddFacultyClick: () => void;
  onEditFacultyClick: (id: string) => void;
  onAddDepartmentClick: (facultyId: string) => void;
  onEditDepartmentClick: (id: string, facultyId: string) => void;
};

export const DepartmentView: React.FC<DepartmentViewProps> = ({
  data,
  onAddFacultyClick,
  onEditFacultyClick,
  onAddDepartmentClick,
  onEditDepartmentClick,
}) => {
  return (
    <Box p={2}>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onAddFacultyClick}
      >
        Add Faculty/School
      </Button>
      <Box mt={2} />
      {data?.map((organization: Organization) => (
        <Box
          key={organization._id}
          mb={4}
          p={2}
          border={1}
          borderColor="grey.300"
          borderRadius="8px"
        >
          <Grid container alignItems="center">
            <Grid size={3}>
              <Typography variant="h6">
                {organization.abbreviation} - {organization.title}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="h6">
                {organization.school ? 'School' : 'Faculty'}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Chip
                label={
                  getStatus(
                    organization.isActive,
                    organization.effectiveFrom,
                    organization.effectiveTo,
                  ).label
                }
                color={
                  getStatus(
                    organization.isActive,
                    organization.effectiveFrom,
                    organization.effectiveTo,
                  ).color
                }
              />
            </Grid>
            <Grid container alignItems="center" size={3}>
              <IconButton
                onClick={() => {
                  onEditFacultyClick(organization._id);
                }}
              >
                <EditIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Divider sx={{ marginY: 2 }} />

          {organization.departments && organization.departments.length > 0 && (
            <List>
              {organization.departments.map(
                (department: Department, index: number) => (
                  <React.Fragment key={department.id}>
                    <ListItem>
                      <Grid container alignItems="center">
                        <Grid size={3}>
                          <Typography>
                            {department.abbreviation} - {department.title}
                          </Typography>
                        </Grid>
                        <Grid size={3}>
                          <Typography>
                            {department.school ? 'School' : 'Department'}
                          </Typography>
                        </Grid>
                        <Grid size={3}>
                          <Chip
                            label={
                              getStatus(
                                department.isActive,
                                department.effectiveFrom,
                                department.effectiveTo,
                              ).label
                            }
                            color={
                              getStatus(
                                department.isActive,
                                department.effectiveFrom,
                                department.effectiveTo,
                              ).color
                            }
                          />
                        </Grid>
                        <Grid container alignItems="center" size={3}>
                          <IconButton
                            onClick={() => {
                              onEditDepartmentClick(
                                department.id,
                                organization._id,
                              );
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                    {organization.departments &&
                      index < organization.departments.length - 1 && (
                        <Divider />
                      )}
                  </React.Fragment>
                ),
              )}
            </List>
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              onAddDepartmentClick(organization._id);
            }}
          >
            Add Department/School
          </Button>
        </Box>
      ))}
    </Box>
  );
};
